import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import { SubmissionError } from "redux-form";
import { message } from "antd";
import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import UserApi from "ee/api/UserApi";
import { toast } from "design-system";
import type { ApiResponse } from "../../api/ApiResponses";
export interface LoginFormValues {
  email?: string;
  name?: string;
  password?: string;
  remember?: string;
  captcha?: string;
  activeKey?: string;
}

export interface SignupFormValues {
  email?: string;
  password?: string;
  name?: string;
  captcha?: string;
}

export interface ResetPasswordFormValues {
  password?: string;
  token?: string;
  email?: string;
  captcha?: string;
}

export type CreatePasswordFormValues = ResetPasswordFormValues;

export interface ForgotPasswordFormValues {
  email?: string;
  verifyCode?: string;
}

export const signupFormSubmitHandler = async (
  values: SignupFormValues,
  dispatch: any,
): Promise<any> => {
  const { email, password } = values;
  return new Promise((resolve, reject) => {
    dispatch({
      type: ReduxActionTypes.CREATE_USER_INIT,
      payload: {
        resolve,
        reject,
        email,
        password,
      },
    });
  }).catch((error) => {
    throw new SubmissionError(error);
  });
};

export const resetPasswordSubmitHandler = async (
  values: ResetPasswordFormValues,
  dispatch: any,
): Promise<any> => {
  const { email, password, token } = values;
  return new Promise((resolve, reject) => {
    dispatch({
      type: ReduxActionTypes.RESET_USER_PASSWORD_INIT,
      payload: {
        resolve,
        reject,
        token,
        email,
        password,
      },
    });
  }).catch((error) => {
    throw new SubmissionError(error);
  });
};

export const createPasswordSubmitHandler = async (
  values: CreatePasswordFormValues,
  dispatch: any,
): Promise<any> => {
  const { email, password, token } = values;
  return new Promise((resolve, reject) => {
    dispatch({
      type: ReduxActionTypes.INVITED_USER_SIGNUP_INIT,
      payload: {
        resolve,
        reject,
        token,
        email,
        password,
      },
    });
  }).catch((error) => {
    throw new SubmissionError(error);
  });
};

export const forgotPasswordSubmitHandler = async (
  values: ForgotPasswordFormValues,
  dispatch: any,
): Promise<any> => {
  const { email, verifyCode } = values;
  return new Promise((resolve, reject) => {
    dispatch({
      type: ReduxActionTypes.FORGOT_PASSWORD_INIT,
      payload: {
        resolve,
        reject,
        email,
        verifyCode
      },
    });
  }).then((res) => {
    message.open({
      type: "success",
      duration: 5,
      content: `密码重置链接已经发送到您的邮箱 ${email} ，请查收确认`,
      className: "my-msg",
    });
  }).catch((error) => {
    error.email = "";
    throw new SubmissionError(error);
  });
};

export const useResendEmailVerification = (
  email: string | null,
): [() => void, boolean, number] => {
  const [clicks, setClicks] = useState(0);
  const [linkEnabled, setLinkEnabled] = useState(true);

  // Disable the link for 30 seconds when clicked
  useEffect(() => {
    if (linkEnabled === false) {
      setTimeout(() => {
        setLinkEnabled(true);
      }, 30000);
    }
  }, [linkEnabled]);

  const resendVerificationLink = useCallback(() => {
    // Track clicks
    setClicks(clicks + 1);
    setLinkEnabled(false);
    if (!email) {
      const errorMessage = "Email not found for retry verification";
      Sentry.captureMessage(errorMessage);
      toast.show(errorMessage, { kind: "error" });
      return;
    }
    UserApi.resendEmailVerification(email)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .then((response: ApiResponse) => {
        if (!response.responseMeta.success && response.responseMeta.error) {
          const { code, message } = response.responseMeta.error;
          const errorMessage = `${code}: ${message}`;
          toast.show(errorMessage, { kind: "error" });
          return;
        }
        toast.show("Verification email sent!", { kind: "success" });
      })
      .catch((error) => {
        toast.show(error.message, { kind: "error" });
      });
  }, [email, clicks]);
  return [resendVerificationLink, linkEnabled, clicks];
};
